import type {AplicarCarga, ResultadoDeCarga, VistaPreviaDeCarga} from '@cnc/contracts';
import {useState} from 'react';
import {Link} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {useAplicarCarga, useCargas, useSubirControlDeEmpaque} from '../api/cargas';
import {AYUDA_BIG_BAG, AYUDA_CARGAS} from '../componentes/ayuda-inventario';
import {CajaAjustable} from '../componentes/caja-ajustable';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PistaDeSoltar, ZonaParaSoltar} from '../componentes/zona-para-soltar';
import {tr, type Texto} from '../i18n/idioma';
import {fecha, fechaHora, mes, numero, textoOGuion} from '../utilidades/formato';

// Un archivo elegido: su recoleccion escrita a mano (vacia = la del archivo),
// su revision y, si ya se aplico, el resultado.
type Entrada = {
  clave: string;
  archivo: File;
  recoleccion: string;
  vista: VistaPreviaDeCarga | null;
  resultado: ResultadoDeCarga | null;
  error: string | null;
  // Lo que se elige antes de aplicar (Daniel, 26/10/2026): el mes de molienda
  // nuevo por exportador|mina|mes y la entrada a bodega de la recoleccion.
  eleccion: Eleccion;
};
type Eleccion = {meses: Record<string, string>; entradaABodega: string};
const SIN_ELECCION: Eleccion = {meses: {}, entradaABodega: ''};
const llaveDeMes = (g: {exportador: string; mina: string}, mes: string) => `${g.exportador}|${g.mina}|${mes}`;
const pedidoDe = (e: Entrada): AplicarCarga => ({
  cambiosDeMolienda: (e.vista?.gruposDeMolienda ?? []).flatMap(g =>
    g.meses
      .map(m => ({exportador: g.exportador, mina: g.mina, desde: m.mes, hacia: e.eleccion.meses[llaveDeMes(g, m.mes)]}))
      .filter((c): c is typeof c & {hacia: string} => !!c.hacia && c.hacia !== c.desde),
  ),
  entradaABodega: e.eleccion.entradaABodega || undefined,
});

const claveDe = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
const mensajeDe = (error: unknown, porDefecto: string) =>
  error instanceof ErrorDeApi ? [error.message, ...(error.detalles ?? [])].join(' ') : porDefecto;

// Carga de controles de empaque de recolecciones: se eligen uno o varios
// archivos, se revisa cada uno y solo entonces se aplican al inventario. La
// recoleccion de cada archivo sale de su nombre o de la columna Shipment #;
// solo si se escribe a mano para ese archivo se usa la escrita.
export function PaginaCargas() {
  const [entradas, setEntradas] = useState<Entrada[]>([]);
  const [fechaRecoleccion, setFechaRecoleccion] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const subir = useSubirControlDeEmpaque();
  const aplicar = useAplicarCarga();
  const cargas = useCargas();

  const actualizar = (clave: string, cambios: Partial<Entrada>) =>
    setEntradas(actual => actual.map(e => (e.clave === clave ? {...e, ...cambios} : e)));

  const agregar = (archivos: File[]) => {
    setEntradas(actual => {
      const claves = new Set(actual.map(e => e.clave));
      const nuevas = archivos
        .filter(f => !claves.has(claveDe(f)))
        .map(f => ({
          clave: claveDe(f),
          archivo: f,
          recoleccion: '',
          vista: null,
          resultado: null,
          error: null,
          eleccion: SIN_ELECCION,
        }));
      // Los ya aplicados salen de la lista al elegir archivos nuevos.
      return [...actual.filter(e => !e.resultado), ...nuevas];
    });
  };

  const revisarUna = async (e: Entrada) => {
    try {
      const vista = await subir.mutateAsync({
        archivo: e.archivo,
        opciones: {
          recoleccion: e.recoleccion.trim() ? e.recoleccion.trim().toUpperCase() : undefined,
          fechaRecoleccion: fechaRecoleccion || undefined,
        },
      });
      // La entrada a bodega viene propuesta (la del archivo o la descarga del camion).
      actualizar(e.clave, {
        vista,
        error: null,
        resultado: null,
        eleccion: {meses: {}, entradaABodega: vista.entradaABodegaSugerida ?? ''},
      });
    } catch (error) {
      actualizar(e.clave, {
        vista: null,
        error: mensajeDe(error, tr('No se pudo leer el archivo.', 'Could not read the file.')),
      });
    }
  };

  const revisarTodas = async () => {
    setOcupado(true);
    // Una por una: dos archivos de la misma recoleccion se revisan en orden.
    for (const e of entradas.filter(x => !x.resultado)) await revisarUna(e);
    setOcupado(false);
  };

  const aplicarUna = async (e: Entrada) => {
    if (!e.vista) return;
    try {
      const resultado = await aplicar.mutateAsync({id: e.vista.id, pedido: pedidoDe(e)});
      actualizar(e.clave, {resultado, vista: null, error: null});
    } catch (error) {
      actualizar(e.clave, {
        error: mensajeDe(error, tr('No se pudo aplicar la carga.', 'Could not apply the upload.')),
      });
    }
  };

  const listas = entradas.filter(e => e.vista?.aplicable && !e.resultado);
  const aplicarTodas = async () => {
    setOcupado(true);
    for (const e of listas) await aplicarUna(e);
    setOcupado(false);
  };
  const porRevisar = entradas.filter(e => !e.resultado).length;

  return (
    <section className="max-w-6xl">
      <Link to="/inventario" className="text-sm text-verde hover:underline">
        {tr('← Volver al inventario', '← Back to inventory')}
      </Link>
      <h1 className="mt-3 text-2xl font-semibold text-verde-oscuro">
        {tr('Cargar control de empaque', 'Upload packing control')}
      </h1>
      <p className="text-sm text-apagado">
        {tr(
          'Elige uno o varios formatos de control de empaque de recolecciones (por ejemplo el de BV y el de MM de la misma recolección). Cada archivo se revisa por separado; nada entra al inventario hasta que confirmes. Las bolsas nuevas quedan disponibles; las que ya existen se actualizan sin cambiar su exportación ni su estado.',
          'Choose one or several pickup packing control files (for example the BV and MM files of the same pickup). Each file is reviewed separately; nothing enters the inventory until you confirm. New bags become available; existing ones are updated without changing their export or status.',
        )}
      </p>

      <div className="mt-4 grid gap-3 rounded-lg border border-linea bg-white p-4 md:grid-cols-[2fr_1fr_auto]">
        <ZonaParaSoltar>
          <label className="block text-xs text-apagado">
            {tr('Archivo de Excel (uno o varios)', 'Excel file (one or several)')}
            <NotaDeAyuda texto={AYUDA_CARGAS.archivo} dentroDeEtiqueta />
            <input
              type="file"
              multiple
              accept=".xlsx,.xls,.xlsm"
              aria-label={tr('Archivo de Excel', 'Excel file')}
              onChange={e => {
                agregar([...(e.target.files ?? [])]);
                // Permite volver a elegir el mismo archivo.
                e.target.value = '';
              }}
              className="mt-1 block w-full text-sm file:mr-3 file:rounded-md file:border file:border-linea file:bg-papel file:px-3 file:py-1.5 file:text-sm"
            />
            <PistaDeSoltar multiple />
          </label>
        </ZonaParaSoltar>
        <label className="block text-xs text-apagado">
          {tr('Fecha de recolección (opcional, para todos)', 'Pickup date (optional, for all)')}
          <NotaDeAyuda texto={AYUDA_CARGAS.fechaRecoleccion} dentroDeEtiqueta />
          <input
            type="date"
            value={fechaRecoleccion}
            onChange={e => setFechaRecoleccion(e.target.value)}
            className="mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta"
          />
        </label>
        <div className="flex items-end gap-2">
          <button
            type="button"
            onClick={() => void revisarTodas()}
            disabled={!porRevisar || ocupado}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {ocupado && subir.isPending
              ? tr('Leyendo…', 'Reading…')
              : porRevisar > 1
                ? tr(`Revisar archivos (${porRevisar})`, `Review files (${porRevisar})`)
                : tr('Revisar archivo', 'Review file')}
          </button>
        </div>
      </div>
      {listas.length > 1 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-verde/40 bg-verde-claro px-4 py-3 text-sm">
          <span>
            {tr(
              `${numero(listas.length)} archivos revisados y listos para aplicar.`,
              `${numero(listas.length)} files reviewed and ready to apply.`,
            )}
          </span>
          <button
            type="button"
            onClick={() => void aplicarTodas()}
            disabled={ocupado}
            className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
          >
            {tr(
              `Aplicar los ${numero(listas.length)} al inventario`,
              `Apply all ${numero(listas.length)} to inventory`,
            )}
          </button>
        </div>
      )}

      {entradas.map(e => (
        <ArchivoElegido
          key={e.clave}
          entrada={e}
          ocupado={ocupado || subir.isPending || aplicar.isPending}
          onRecoleccion={valor => actualizar(e.clave, {recoleccion: valor})}
          onEleccion={eleccion => actualizar(e.clave, {eleccion})}
          onRevisar={() => void revisarUna(e)}
          onAplicar={() => void aplicarUna(e)}
          onQuitar={() => setEntradas(actual => actual.filter(x => x.clave !== e.clave))}
        />
      ))}

      <div className="mt-6 rounded-lg border border-linea bg-white p-4">
        <h2 className="font-medium">{tr('Últimas cargas', 'Latest uploads')}</h2>
        {cargas.data && cargas.data.cargas.length === 0 && (
          <p className="mt-2 text-sm text-apagado">
            {tr('Todavía no se ha cargado ningún archivo desde la app.', 'No file has been uploaded from the app yet.')}
          </p>
        )}
        {cargas.data && cargas.data.cargas.length > 0 && (
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="py-1 font-semibold">
                  {tr('Fecha', 'Date')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.fecha} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Tipo', 'Type')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.tipo} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Archivo', 'File')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.nombreArchivo} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Recolección', 'Pickup')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.recoleccionCarga} />
                </th>
                <th className="py-1 font-semibold">
                  {tr('Estado', 'Status')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.estado} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Filas', 'Rows')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.filas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Nuevas', 'New')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.nuevas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Actualizadas', 'Updated')}
                  <NotaDeAyuda texto={AYUDA_CARGAS.actualizadas} />
                </th>
              </tr>
            </thead>
            <tbody>
              {cargas.data.cargas.map(c => (
                <tr key={c.id} className="border-t border-linea/70">
                  <td className="py-1">{fechaHora(c.creadaEl)}</td>
                  <td className="py-1">{c.tipoEtiqueta}</td>
                  <td className="max-w-64 truncate py-1" title={c.archivo ?? ''}>
                    {textoOGuion(c.archivo)}
                  </td>
                  <td className="py-1">{textoOGuion(c.recoleccion)}</td>
                  <td className="py-1">
                    {c.estadoEtiqueta}
                    {c.error ? ` · ${c.error}` : ''}
                  </td>
                  <td className="py-1 text-right tabular-nums">{numero(c.filas)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(c.nuevas)}</td>
                  <td className="py-1 text-right tabular-nums">{numero(c.actualizadas)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function ArchivoElegido({
  entrada: e,
  ocupado,
  onRecoleccion,
  onEleccion,
  onRevisar,
  onAplicar,
  onQuitar,
}: {
  entrada: Entrada;
  ocupado: boolean;
  onRecoleccion: (valor: string) => void;
  onEleccion: (eleccion: Eleccion) => void;
  onRevisar: () => void;
  onAplicar: () => void;
  onQuitar: () => void;
}) {
  if (e.resultado)
    return (
      <div className="mt-4 rounded-lg border border-verde/40 bg-verde-claro p-4 text-sm">
        <div className="font-medium text-verde-oscuro">
          {tr(
            `Carga aplicada a la recolección ${e.resultado.recoleccion}`,
            `Upload applied to pickup ${e.resultado.recoleccion}`,
          )}
          <span className="ml-2 text-xs font-normal text-apagado">{e.archivo.name}</span>
        </div>
        <div className="mt-1">
          {tr(
            `${numero(e.resultado.nuevas)} Big Bags nuevos y ${numero(e.resultado.actualizadas)} actualizados. La recolección tiene ahora ${numero(e.resultado.bigBagsEnRecoleccion)} Big Bags.`,
            `${numero(e.resultado.nuevas)} new Big Bags and ${numero(e.resultado.actualizadas)} updated. The pickup now has ${numero(e.resultado.bigBagsEnRecoleccion)} Big Bags.`,
          )}{' '}
          {e.resultado.mesesCambiados > 0 &&
            tr(
              `${numero(e.resultado.mesesCambiados)} con el mes de molienda cambiado. `,
              `${numero(e.resultado.mesesCambiados)} with the grinding month changed. `,
            )}
          {e.resultado.conEntradaABodega > 0 &&
            tr(
              `${numero(e.resultado.conEntradaABodega)} con la entrada a bodega puesta. `,
              `${numero(e.resultado.conEntradaABodega)} with the warehouse entry date set. `,
            )}
          <Link
            to={`/inventario?recoleccion=${encodeURIComponent(e.resultado.recoleccion)}`}
            className="text-verde hover:underline"
          >
            {tr('Verlos en el inventario', 'View them in the inventory')}
          </Link>
        </div>
      </div>
    );

  return (
    <div
      className="mt-4 space-y-3 rounded-lg border border-linea bg-white p-4"
      aria-label={tr(`Archivo ${e.archivo.name}`, `File ${e.archivo.name}`)}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium" title={e.archivo.name}>
            {e.archivo.name}
          </div>
          {!e.vista && !e.error && (
            <div className="text-xs text-apagado">{tr('Sin revisar todavía.', 'Not reviewed yet.')}</div>
          )}
        </div>
        <label className="block text-xs text-apagado">
          {tr('Recolección (S-número)', 'Pickup (S-number)')}
          <NotaDeAyuda texto={AYUDA_CARGAS.recoleccion} dentroDeEtiqueta />
          <input
            value={e.recoleccion}
            onChange={ev => onRecoleccion(ev.target.value)}
            placeholder={e.vista?.recoleccion ?? tr('Se toma del archivo', 'Taken from the file')}
            title={tr(
              'Déjala vacía para usar la del archivo; escríbela solo si el archivo no la trae o la trae mal.',
              'Leave it empty to use the one in the file; enter it only if the file has none or a wrong one.',
            )}
            className="mt-1 w-40 rounded-md border border-linea px-2 py-1.5 text-sm text-tinta"
          />
        </label>
        {e.vista && (
          <button
            type="button"
            onClick={onRevisar}
            disabled={ocupado}
            className="rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50"
          >
            {tr('↻ Volver a revisar', '↻ Review again')}
          </button>
        )}
        <button type="button" onClick={onQuitar} className="px-2 py-1.5 text-sm text-apagado hover:text-alerta">
          {tr('Quitar', 'Remove')}
        </button>
      </div>
      {e.error && <p className="text-sm text-alerta">{e.error}</p>}
      {e.vista && (
        <VistaPrevia
          vista={e.vista}
          eleccion={e.eleccion}
          onEleccion={onEleccion}
          onConfirmar={onAplicar}
          aplicando={ocupado}
        />
      )}
    </div>
  );
}

function VistaPrevia({
  vista,
  eleccion,
  onEleccion,
  onConfirmar,
  aplicando,
}: {
  vista: VistaPreviaDeCarga;
  eleccion: Eleccion;
  onEleccion: (eleccion: Eleccion) => void;
  onConfirmar: () => void;
  aplicando: boolean;
}) {
  const conAviso = vista.gruposDeMolienda.filter(g => g.conAviso);
  const elegirMes = (llave: string, mesNuevo: string) =>
    onEleccion({...eleccion, meses: {...eleccion.meses, [llave]: mesNuevo}});
  const chips: Array<[string, string, Texto]> = [
    [tr('Filas', 'Rows'), numero(vista.filas), AYUDA_CARGAS.filas],
    [tr('Nuevas', 'New'), numero(vista.nuevas), AYUDA_CARGAS.nuevas],
    [tr('Existentes', 'Existing'), numero(vista.existentes), AYUDA_CARGAS.existentes],
    [tr('Ton. húmedas', 'Wet tons'), numero(vista.totales.tonHumedas, 3), AYUDA_CARGAS.totales],
    [tr('Ton. secas', 'Dry tons'), numero(vista.totales.tonSecas, 3), AYUDA_CARGAS.totales],
    ['Au (g)', numero(vista.totales.auG, 2), AYUDA_CARGAS.totales],
    ['Ag (g)', numero(vista.totales.agG, 2), AYUDA_CARGAS.totales],
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="font-medium">
            {tr(
              `${vista.tipoDetectado} · recolección ${vista.recoleccion ?? 'sin definir'}`,
              `${vista.tipoDetectado} · pickup ${vista.recoleccion ?? 'not set'}`,
            )}
            {vista.recoleccion && (
              <span className="ml-2 text-xs text-apagado">
                {vista.recoleccionExiste ? tr('ya existe', 'already exists') : tr('nueva', 'new')}
              </span>
            )}
          </div>
          <div className="text-xs text-apagado">
            {tr(`hojas: ${vista.hojas.join(', ') || '—'}`, `sheets: ${vista.hojas.join(', ') || '—'}`)}
          </div>
        </div>
        <button
          type="button"
          onClick={onConfirmar}
          disabled={!vista.aplicable || aplicando}
          className="rounded-md bg-verde px-4 py-2 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50"
        >
          {aplicando
            ? tr('Aplicando…', 'Applying…')
            : vista.aplicable
              ? tr('Aplicar al inventario', 'Apply to inventory')
              : tr('Corrige los errores', 'Fix the errors')}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
        {chips.map(([nombre, valor, ayuda]) => (
          <div key={nombre} className="rounded-md border border-linea bg-papel px-3 py-1.5">
            <div className="text-[11px] text-apagado">
              {nombre}
              <NotaDeAyuda titulo={nombre} texto={ayuda} />
            </div>
            <div className="text-sm font-semibold tabular-nums text-verde-oscuro">{valor}</div>
          </div>
        ))}
      </div>
      {vista.errores.length > 0 && (
        <ul className="space-y-1 text-sm text-alerta">
          {vista.errores.map(e => (
            <li key={e}>✕ {e}</li>
          ))}
        </ul>
      )}
      {vista.alertasDeMolienda.length > 0 && (
        <div
          className="rounded-lg border border-dorado/60 bg-dorado-claro px-4 py-3 text-sm"
          role="alert"
          data-prueba="alertas-de-molienda"
        >
          <div className="font-medium">
            ⚠ {tr('Mes de molienda inusual', 'Unusual grinding month')}
            <NotaDeAyuda texto={AYUDA_CARGAS.mesInusual} />
          </div>
          <ul className="mt-1 space-y-0.5">
            {vista.alertasDeMolienda.map(a => (
              <li key={a}>{a}</li>
            ))}
          </ul>
          {/* Ajustar el mes ahi mismo (Daniel, 26/10/2026): cada mes del archivo
              puede quedar como viene o pasar a otro mes. */}
          {conAviso.length > 0 && (
            <div className="mt-3 space-y-2" data-prueba="ajustar-molienda">
              <div className="text-xs text-apagado">
                {tr(
                  'Si quieres, cambia aquí el mes de molienda de esos Big Bags antes de aplicar. Lo que no cambies queda como viene en el archivo.',
                  'If you want, change the grinding month of those Big Bags here before applying. Anything you do not change stays as in the file.',
                )}
              </div>
              {conAviso.map(g => (
                <div key={`${g.exportador}|${g.mina}`} className="rounded-md bg-white/70 px-3 py-2">
                  <div className="font-medium">
                    {g.exportador} · {g.mina}
                    {g.ultimoCargado && (
                      <span className="ml-2 text-xs font-normal text-apagado">
                        {tr(`lo último cargado: ${mes(g.ultimoCargado)}`, `last loaded: ${mes(g.ultimoCargado)}`)}
                      </span>
                    )}
                  </div>
                  {g.meses.map(m => {
                    const llave = llaveDeMes(g, m.mes);
                    const elegido = eleccion.meses[llave] ?? m.mes;
                    return (
                      <div key={m.mes} className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="w-48">
                          {tr(
                            `${numero(m.bigBags)} BB de ${mes(m.mes)} →`,
                            `${numero(m.bigBags)} BB from ${mes(m.mes)} →`,
                          )}
                        </span>
                        <input
                          type="month"
                          value={elegido}
                          onChange={ev => elegirMes(llave, ev.target.value || m.mes)}
                          aria-label={tr(
                            `Mes de molienda para los BB de ${mes(m.mes)} de ${g.mina}`,
                            `Grinding month for the ${mes(m.mes)} BB of ${g.mina}`,
                          )}
                          className="rounded-md border border-linea px-2 py-1 text-sm text-tinta"
                        />
                        {g.ultimoCargado && g.ultimoCargado !== m.mes && elegido !== g.ultimoCargado && (
                          <button
                            type="button"
                            onClick={() => elegirMes(llave, g.ultimoCargado!)}
                            className="text-xs text-verde hover:underline"
                          >
                            {tr(`Pasar a ${mes(g.ultimoCargado)}`, `Move to ${mes(g.ultimoCargado)}`)}
                          </button>
                        )}
                        {elegido !== m.mes && (
                          <>
                            <span className="text-xs font-medium text-verde-oscuro">{tr('cambia', 'changes')}</span>
                            <button
                              type="button"
                              onClick={() => elegirMes(llave, m.mes)}
                              className="text-xs text-apagado hover:text-tinta"
                            >
                              {tr('Dejar como viene', 'Keep as in the file')}
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {vista.recoleccion && (
        <label className="flex flex-wrap items-center gap-2 text-sm" data-prueba="entrada-a-bodega">
          <span>
            {tr(
              `Fecha de entrada a bodega de ${vista.recoleccion}`,
              `Warehouse entry date of pickup ${vista.recoleccion}`,
            )}
            <NotaDeAyuda texto={AYUDA_CARGAS.entradaABodega} />
          </span>
          <input
            type="date"
            value={eleccion.entradaABodega}
            onChange={ev => onEleccion({...eleccion, entradaABodega: ev.target.value})}
            className="rounded-md border border-linea px-2 py-1 text-sm text-tinta"
          />
          <span className="text-xs text-apagado">
            {eleccion.entradaABodega
              ? tr(
                  `Se pone en todos sus Big Bags (${numero(vista.bigBagsYaEnRecoleccion + vista.nuevas)}).`,
                  `It is set on all its Big Bags (${numero(vista.bigBagsYaEnRecoleccion + vista.nuevas)}).`,
                )
              : tr('Vacía: no se cambia.', 'Empty: not changed.')}
          </span>
        </label>
      )}
      {vista.advertencias.length > 0 && (
        <ul className="space-y-1 text-sm text-tinta">
          {vista.advertencias.map(a => (
            <li key={a}>! {a}</li>
          ))}
        </ul>
      )}
      {vista.diferencias.length > 0 && (
        <details className="rounded-lg border border-linea bg-white" open data-prueba="diferencias-de-carga">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium">
            {tr(
              `Qué cambia en los ${vista.bigBagsQueCambian} Big Bags que ya estaban`,
              `What changes in the ${vista.bigBagsQueCambian} Big Bags that were already there`,
            )}
            <NotaDeAyuda texto={AYUDA_CARGAS.diferencias} />
          </summary>
          <CajaAjustable altoMaximo="16rem" className="overflow-auto border-t border-linea">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-papel">
                <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                  <th className="px-3 py-1.5 font-semibold">BB #</th>
                  <th className="px-3 py-1.5 font-semibold">{tr('Dato', 'Field')}</th>
                  <th className="px-3 py-1.5 font-semibold">{tr('Hoy en la app', 'Today in the app')}</th>
                  <th className="px-3 py-1.5 font-semibold">{tr('Queda con el archivo', 'With the file')}</th>
                </tr>
              </thead>
              <tbody>
                {vista.diferencias.map(d => (
                  <tr key={`${d.bbNo}-${d.campo}`} className="border-t border-linea/70">
                    <td className="px-3 py-1">{d.bbNo}</td>
                    <td className="px-3 py-1 text-apagado">{d.campo}</td>
                    <td className="px-3 py-1 line-through decoration-alerta/60">{d.antes ?? '—'}</td>
                    <td className="px-3 py-1 font-medium">{d.despues ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CajaAjustable>
        </details>
      )}
      <CajaAjustable altoMaximo="22rem" className="overflow-auto rounded-lg border border-linea bg-white">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-papel">
            <tr className="text-left text-xs uppercase tracking-wide text-apagado">
              <th className="px-3 py-2 font-semibold">
                BB #<NotaDeAyuda texto={AYUDA_BIG_BAG.bbNo} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Resultado', 'Result')}
                <NotaDeAyuda texto={AYUDA_CARGAS.resultado} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Sello', 'Seal')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.sello} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Tipo', 'Type')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.tipoMaterial} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Empaque', 'Packing')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.fechaEmpaque} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                WMT
                <NotaDeAyuda texto={AYUDA_CARGAS.wmt} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                DMT
                <NotaDeAyuda texto={AYUDA_CARGAS.dmt} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Au g/t
                <NotaDeAyuda texto={AYUDA_BIG_BAG.auGt} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Au g<NotaDeAyuda texto={AYUDA_BIG_BAG.auG} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Ag g/t
                <NotaDeAyuda texto={AYUDA_BIG_BAG.agGt} />
              </th>
              <th className="px-3 py-2 text-right font-semibold">
                Ag g<NotaDeAyuda texto={AYUDA_BIG_BAG.agG} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Molienda', 'Grinding')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.mesMolienda} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Mina', 'Mine')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.mina} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Proveedor', 'Supplier')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.proveedor} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Exportador', 'Exporter')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.exportador} />
              </th>
              <th className="px-3 py-2 font-semibold">
                {tr('Planta', 'Plant')}
                <NotaDeAyuda texto={AYUDA_BIG_BAG.planta} />
              </th>
            </tr>
          </thead>
          <tbody>
            {vista.muestra.map(f => (
              <tr key={f.bbNo} className="border-t border-linea/70">
                <td className="px-3 py-1 font-medium">{f.bbNo}</td>
                <td className="px-3 py-1">
                  {f.resultado === 'nueva' ? (
                    <span className="rounded-full bg-verde-claro px-2 py-0.5 text-xs text-verde-oscuro">
                      {tr('Nueva', 'New')}
                    </span>
                  ) : (
                    <span
                      className="rounded-full bg-dorado-claro px-2 py-0.5 text-xs"
                      title={
                        f.exportacionActual
                          ? tr(`Asignada a ${f.exportacionActual}`, `Assigned to ${f.exportacionActual}`)
                          : ''
                      }
                    >
                      {tr('Existente', 'Existing')}
                      {f.estadoActual && f.estadoActual !== 'available'
                        ? ` · ${f.exportacionActual ?? f.estadoActual}`
                        : ''}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1">{textoOGuion(f.sello)}</td>
                <td className="px-3 py-1">{textoOGuion(f.tipoMaterial)}</td>
                <td className="px-3 py-1">{fecha(f.fechaEmpaque)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{numero(f.tonHumedas, 3)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{numero(f.tonSecas, 3)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{numero(f.auGt, 2)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{numero(f.auG, 2)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{numero(f.agGt, 2)}</td>
                <td className="px-3 py-1 text-right tabular-nums">{numero(f.agG, 2)}</td>
                <td className="px-3 py-1">{mes(f.mesMolienda)}</td>
                <td className="px-3 py-1">{textoOGuion(f.mina)}</td>
                <td className="px-3 py-1">{textoOGuion(f.proveedor)}</td>
                <td className="px-3 py-1">{textoOGuion(f.exportador)}</td>
                <td className="px-3 py-1">{textoOGuion(f.planta)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {vista.filas > vista.muestra.length && (
          <div className="px-3 py-2 text-xs text-apagado">
            {tr(
              `Se muestran ${numero(vista.muestra.length)} de ${numero(vista.filas)} filas.`,
              `Showing ${numero(vista.muestra.length)} of ${numero(vista.filas)} rows.`,
            )}
          </div>
        )}
      </CajaAjustable>
    </div>
  );
}
